import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import LinkIcon from '@mui/icons-material/Link';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import './style.css';
import { dispatchErr } from '../../util/metamask';
import { useSnackbar } from 'notistack';
import { MODAL_TOGGLE } from '../../redux/actions/app';
import { ID as MINT_ID } from '../Mint/config';
import { SET_ACCOUNTS, SET_NETWORK } from '../../redux/actions/eth';
import MetaMaskOnboarding from '@metamask/onboarding';
import { MintIcon } from '../../util/icons';

const ConnectMetaMask = () => {

    const dispatch = useDispatch(),
    { enqueueSnackbar } = useSnackbar(),
    labels = useMemo(() => new LocalizedStrings( LOCALIZATIONS ), []),
    locale = useSelector( state => state.app.locale ),
    { isNew, name } = useSelector( state => state.board ),
    onboarding = useRef( null ),
    [ connected, setConnected ] = useState( false ),
    [ label, setLabel ] = useState( '' );

    /**
     * @name handleNetworkChange
     * @function
     * @description Sets the current network
     * @param {number} payload 
     */
    const handleNetworkChange = useCallback( payload => {
        dispatch({ type: SET_NETWORK, payload });
    }, [ dispatch ]);

    /**
     * @name getNetworks
     * @function
     * @description Gets the network
     */
     const getNetworks = useCallback(async () => {
        window.ethereum.on( 'chainChanged', handleNetworkChange );
        const network = await window.ethereum.request({ method: 'eth_chainId' });
        handleNetworkChange( network );
    }, [ handleNetworkChange ]);

    /**
     * @name handleConnect
     * @function
     * @description passes accounts where they need to go when the user is connected
     */
    const handleConnect = useCallback( payload => {
        setLabel( 'mint' );
        setConnected( true );
        getNetworks();
        dispatch({ type: SET_ACCOUNTS, payload });
    }, [ dispatch, setLabel, setConnected, getNetworks ]);

    /**
     * @name connectAuto
     * @function
     * @description Initiates an auto connection attempt (this just sees if you've connected in the past)
     */
    const connectAuto = useCallback( async () => {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if( accounts.length > 0 ){
            handleConnect( accounts );
        } else {
            setLabel( 'connect' );
        }
    }, [ setLabel, handleConnect ]);

    /**
     * @name connectManual
     * @function
     * @description Initiates a manual connection attempt
     */
    const connectManual = async () => {
        try {
            if( MetaMaskOnboarding.isMetaMaskInstalled() ){
                const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
                onboarding.current.stopOnboarding();
                handleConnect( accounts );
            }
        } catch( err ) {
            dispatchErr( err, enqueueSnackbar, labels );
        }
    }

    /**
     * @name onClick
     * @function
     * @description Handles what happens on click of the button
     */
    const onClick = () => {

        // direct user to metamask download page
        if( typeof window.ethereum === 'undefined' || !window.ethereum.isMetaMask ){
            const onboarding = new MetaMaskOnboarding();
            onboarding.startOnboarding();
            
        // connect to wallet
        } else if( !connected ){
            connectManual();

        // initiate creation
        } else {
            dispatch({ type: MODAL_TOGGLE, payload: MINT_ID });
            
        }
    }

    // show the correct 
    useEffect(() => {
        if( typeof window.ethereum === 'undefined' ){
            setLabel( 'install' );
        } else {
            connectAuto();
        }
    }, [ setLabel, connectAuto ]);
   
    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale )
    }, [ labels, locale ]);

    // create initial object
    useEffect(() => {
        window.ethereum?.on( 'accountsChanged', handleConnect );
        if( !onboarding.current ) onboarding.current = new MetaMaskOnboarding();
        return () => {
            window.ethereum?.removeListener( 'accountsChanged', handleConnect );
        }
    }, [ handleConnect ]);

    return (
        <Button aria-label={ labels[ label ] } disabled={ ( label === 'mint' && ( isNew || name === '' ) ) } onClick={ onClick } color="inherit" startIcon={ 
            label === 'install' ? 
                <InstallDesktopIcon /> : 
                label === 'connect' ? 
                    <LinkIcon />  : 
                    label === 'mint' ?
                        <MintIcon /> :
                        null
        }>
            { labels[ label ] }
        </Button>
    )
}

export default ConnectMetaMask;